import SimpleTable from '@/components/common/tables/simpleTable';
import React from 'react';
import { ClientDirectoryHeader } from '../ClientDirectoryHeader';
import { Avatar, Breadcrumb } from '@/components/common';
import Icons from '@/const/icons';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { CleanupWizard, StatusCleanupWizard } from '@/interfaces';
import AppRoutes from '@/const/routes';
import { SummaryCard, SummaryTypes } from '@/components/dashboard';
import { ReasonFilter, StatusFilter, ClientNameFilter } from './filters';
import { StatusTableColumn } from './StatusTableColumn';
import { ActionsTableColumn } from './ActionsTableColumn';
import { GET_CLEANUP_WIZARD, GET_CLEANUP_WIZARD_METRICS } from '@/api';
import { cleanupWizardService } from '@/api/cleanupWizard';
import { capitalizeWordWithExceptions, formatWithCommas } from '@/lib';
import { EmptyStateFilter } from '../../common/EmptyStateFilter';
// import { ClientNameFilter } from '../filters';

const paths = [
	{
		icon: Icons.book,
		href: AppRoutes.LAWYERAPP_CLIENT_DIRECTORY,
		name: 'Client Directory',
		current: false,
	},
	{
		icon: Icons.flag,
		href: '',
		name: 'Client Cleanup Wizard',
		current: true,
	},
];

export interface CleanupMetricsI {
	type: SummaryTypes;
	amount: string;
}

export const CleanupWizardScreen: React.FC = () => {
	const LIMIT_PAGE = 10;
	const tableRef = React.useRef<HTMLDivElement | null>(null);
	const router = useRouter();
	const { page, search, status, reason, clientId } = router.query;
	const [cleanupMetrics, setCleanupWizard] = React.useState<CleanupMetricsI[]>(
		[]
	);

	const activeFilters = {
		hasSearch: !!search,
		hasStatusFilter: !!status,
		hasReasonFilter: !!reason,
	};

	const { data: cleanupWizardList, refetch: refetchCleanupWizard } = useQuery({
		queryKey: [
			GET_CLEANUP_WIZARD,
			page,
			search,
			status,
			reason,
			router,
			clientId,
		],
		queryFn: () =>
			cleanupWizardService.getCleanupWizardList(
				Number(page || '1'),
				LIMIT_PAGE,
				search ? encodeURIComponent(search as string) : undefined,
				reason ? capitalizeWordWithExceptions(reason as string) : undefined,
				status
					? (capitalizeWordWithExceptions(
							status as string
					  ) as StatusCleanupWizard)
					: undefined,
				clientId as string
			),
	});

	const { data: cleanupWizardMetrics, refetch: refetchMetics } = useQuery({
		queryKey: [GET_CLEANUP_WIZARD_METRICS],
		queryFn: () => cleanupWizardService.getCleanupWizardMetrics(),
	});

	React.useEffect(() => {
		if (cleanupWizardMetrics) {
			const auxMetrics: CleanupMetricsI[] = [];
			auxMetrics.push({
				type: 'flagged-accounts',
				amount: cleanupWizardMetrics.data?.flaggedResult?.toString(),
			});
			auxMetrics.push({
				type: 'deactivated-accounts',
				amount: cleanupWizardMetrics.data?.deactiveResult?.toString(),
			});
			auxMetrics.push({
				type: 'review-required',
				amount: cleanupWizardMetrics.data?.reviewResult?.toString(),
			});
			setCleanupWizard(auxMetrics);
		}
	}, [cleanupWizardMetrics]);

	const hasActiveFilters = () => {
		return Object.values(activeFilters).some((value) => value);
	};

	const refetchQueries = () => {
		refetchCleanupWizard();
		refetchMetics();
	};

	const showEmptyState = cleanupWizardList?.data?.items.length === 0;

	return (
		<div className="overflow-hidden">
			<Breadcrumb paths={paths} />

			<div className="flex flex-wrap gap-6 mt-8 mb-6">
				{cleanupMetrics.map((opt, index) => (
					<SummaryCard
						key={index}
						type={opt.type as SummaryTypes}
						classNameText="capitalize"
						quantity={formatWithCommas(opt.amount)}
						className="border border-[#0D061614] w-[245px] !py-[18px]"
						iconBgSize="w-12 h-12"
						iconSize="!w-6 !h-6"
						titleTypographyType="regular-12"
					/>
				))}
			</div>
			<div
				className={`bg-white w-full rounded-2xl p-4 ${
					showEmptyState && 'h-[55vh]'
				}`}
				ref={tableRef}
			>
				<ClientDirectoryHeader
					title="Client Cleanup Wizard"
					showingItems={cleanupWizardList?.data.total || 0}
					totalItems={cleanupWizardList?.data.totalItems || 0}
					labelTotalItems="clients"
				/>
				<SimpleTable<CleanupWizard>
					classNameContainer="border-none shadow-none mt-6"
					classNameHead="bg-white !opacity-100"
					classNameHeader="grid grid-cols-4 grid-cols-[repeat(3,_minmax(0,_1fr))_0.5fr]"
					classNameRows="grid grid-cols-4 grid-cols-[repeat(3,_minmax(0,_1fr))_0.5fr] items-center cursor-pointer text-black"
					totalPages={cleanupWizardList?.data?.totalPages}
					hideRowActions
					onClickRow={(value) =>
						router.push(
							`/lawyerapp/client-profile/${value.id}?from=cleanup-wizard`
						)
					}
					columns={[
						{
							header: <ClientNameFilter />,
							content: (instance) => (
								<div className="flex items-center gap-x-2 capitalize">
									<Avatar
										photoUrl={instance.profile_picture || ''}
										size="small"
										firstname={instance.firstname}
										lastname={instance.lastname}
									/>
									{instance.firstname
										? `${instance.firstname} ${instance.lastname}`
										: 'N/N'}
								</div>
							),
						},
						{
							header: <ReasonFilter />,
							content: (instance) => <>{instance.reason}</>,
						},
						{
							header: <StatusFilter />,
							content: (instance) => (
								<StatusTableColumn
									status={instance.status}
									date={instance.audit_date}
								/>
							),
						},
						{
							header: 'Actions',
							content: (instance) => (
								<ActionsTableColumn
									status={instance.status}
									userId={instance.id}
									refetch={refetchQueries}
								/>
							),
						},
					]}
					rows={cleanupWizardList?.data?.items as CleanupWizard[]}
				/>
				{showEmptyState && (
					<>
						{hasActiveFilters() && !activeFilters.hasSearch ? (
							<EmptyStateFilter
								icon={Icons.funnelEmpty}
								iconClassName="w-[190px] h-[126px]"
								message={'There are currently no cases matching this filter.'}
								subMessage={'Please try adjusting your filter.'}
							/>
						) : activeFilters.hasSearch && hasActiveFilters() ? (
							<EmptyStateFilter
								icon={Icons.magnifyingGlass}
								iconClassName="!w-[158px] h-[126px]"
								message={'No results were found for your search.'}
								subMessage={'Please try using different keywords.'}
							/>
						) : (
							<EmptyStateFilter
								icon={Icons.usersEmpty}
								iconClassName="!w-[158px] h-[126px]"
								message={'There are no clients flagged.'}
							/>
						)}
					</>
				)}
			</div>
		</div>
	);
};
